import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Text,
  ActionIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import {
  IconTrash,
  IconAlertCircle,
  IconChevronDown,
} from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import {
  convertTo12HourFormat,
  convertTo24HourFormat,
  toCalendarDay,
} from "../../../utils/timeUtils";
import type { StaffHoursOverride } from "../../../types/scheduleAdjustment";
import styles from "./ScheduleAdjustments.module.css";

/** One weekly-schedule entry, indexed 0 = Sunday. Times are 24h "HH:MM". */
export interface WeeklyDay {
  isWorking: boolean;
  start: string | null;
  end: string | null;
}

export interface ScheduleAdjustmentsProps {
  /** The staff member the adjustments belong to (edit mode only). */
  staffId: string;
  /** Current location UUID (businessInfo.id) for scoping the overrides. */
  businessLocationUuid?: string;
  /** Weekly schedule, 7 entries indexed 0=Sun..6=Sat, for the context line. */
  weeklySchedule: WeeklyDay[];
}

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// New row's default single window: 09:00 AM – 05:00 PM (per interactions §7).
const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";

interface AdjustmentRow {
  /** Stable React key (local only). */
  key: string;
  /** Persisted override id, or null for an unsaved draft. */
  overrideId: string | null;
  /** "YYYY-MM-DD" or "" when no date chosen yet. */
  date: string;
  /** 24h "HH:MM". Both a start and an end are always required. */
  start: string | null;
  end: string | null;
  /** The user edited the times this session (blocks weekly-prefill overwrite). */
  touched: boolean;
  saving: boolean;
  /** Differs from what's persisted (drives the Save button). */
  dirty: boolean;
  /** Save/delete error (network / 4xx / 5xx), shown on the row. */
  error: string | null;
}

let keySeq = 0;
const nextKey = () => `adj-${keySeq++}`;

/** The weekday (0=Sun) for a "YYYY-MM-DD" string, without any UTC shift. */
export const weekdayOf = (date: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d)).getDay();
};

/** The weekly-schedule entry for a date, or null when that day isn't worked. */
export const normalFor = (
  date: string,
  weekly: WeeklyDay[],
): { start: string | null; end: string | null } | null => {
  const wd = weekdayOf(date);
  if (wd == null) return null;
  const entry = weekly[wd];
  if (!entry || !entry.isWorking || !entry.start || !entry.end) return null;
  return { start: entry.start, end: entry.end };
};

/** Grey context line under a row (design §4). */
export const contextLine = (date: string, weekly: WeeklyDay[]): string => {
  if (!date) return "Pick a date to see the normal schedule";
  const wd = weekdayOf(date);
  const weekdayName = wd == null ? "" : WEEKDAY_NAMES[wd];
  const normal = normalFor(date, weekly);
  if (normal) {
    return `Normally ${convertTo12HourFormat(normal.start)} – ${convertTo12HourFormat(
      normal.end,
    )} · ${weekdayName}`;
  }
  return `Normally unavailable · ${weekdayName}`;
};

/** Teal pill label (design §4). */
export const tagFor = (date: string, weekly: WeeklyDay[]): string => {
  if (!date) return "NEW";
  return normalFor(date, weekly) ? "HOURS CHANGED" : "EXTRA DAY";
};

const rowFromOverride = (o: StaffHoursOverride): AdjustmentRow => ({
  key: nextKey(),
  overrideId: o.id,
  date: o.work_date,
  start: o.start_time ? convertTo24HourFormat(o.start_time) : null,
  end: o.end_time ? convertTo24HourFormat(o.end_time) : null,
  touched: true, // persisted rows must never be clobbered by weekly prefill
  saving: false,
  dirty: false,
  error: null,
});

/**
 * Schedule Adjustments — ad-hoc one-off working hours for a single date that
 * overwrite the weekly schedule (design handoff §5.6). Single window per date:
 * a date plus a required start AND end time (day-off is handled elsewhere via
 * time blocks, never here — the backend 400s on a missing time, so the UI
 * never sends a null). Each row persists on its own Save via the dedicated
 * hours-overrides endpoints; the save always succeeds (no appointment-conflict
 * guard), with generic error handling for network/5xx.
 *
 * The existing rows sit in a collapsible section (header + chevron) so a member
 * with many special days stays compact; "+ Add Adjustment" stays visible and
 * expands the section when collapsed.
 *
 * Lives at the foot of the staff modal's Working Hours tab. Rendered only in
 * edit mode — an override needs a real staffId (gated by the caller).
 */
export default function ScheduleAdjustments({
  staffId,
  businessLocationUuid,
  weeklySchedule,
}: ScheduleAdjustmentsProps) {
  const {
    getStaffHoursOverrides,
    createStaffHoursOverride,
    deleteStaffHoursOverride,
  } = useApp();
  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Context CRUD fns aren't memoized (a new identity each AppProvider render).
  // Read them through refs so the load effect can depend only on staff/location
  // and never refetch-loops on an unrelated provider re-render.
  const getOverridesRef = useRef(getStaffHoursOverrides);
  getOverridesRef.current = getStaffHoursOverrides;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getOverridesRef
      .current(staffId, { businessLocationUuid })
      .then((overrides) => {
        if (cancelled) return;
        setRows(overrides.map(rowFromOverride));
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Could not load schedule adjustments.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [staffId, businessLocationUuid]);

  const patchRow = useCallback((key: string, patch: Partial<AdjustmentRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  }, []);

  const handleDateChange = (
    row: AdjustmentRow,
    value: Date | string | null,
  ) => {
    const date = value ? toCalendarDay(value) : "";
    const patch: Partial<AdjustmentRow> = { date, dirty: true, error: null };
    // Prefill from the weekly schedule only when the row is still untouched and
    // the new date is a normally-working day (interactions §7).
    if (!row.touched) {
      const normal = normalFor(date, weeklySchedule);
      if (normal) {
        patch.start = normal.start;
        patch.end = normal.end;
      }
    }
    patchRow(row.key, patch);
  };

  const handleTimeChange = (
    row: AdjustmentRow,
    field: "start" | "end",
    value: string,
  ) => {
    patchRow(row.key, {
      [field]: value || null,
      touched: true,
      dirty: true,
      error: null,
    } as Partial<AdjustmentRow>);
  };

  const handleAdd = () => {
    setExpanded(true);
    setRows((prev) => [
      ...prev,
      {
        key: nextKey(),
        overrideId: null,
        date: "",
        start: DEFAULT_START,
        end: DEFAULT_END,
        touched: false,
        saving: false,
        dirty: true,
        error: null,
      },
    ]);
  };

  const rowValidationError = (row: AdjustmentRow): string | null => {
    if (!row.date) return "Pick a date for this adjustment.";
    if (!row.start || !row.end) return "Set a start and end time.";
    if (row.start >= row.end)
      return "The end time must be after the start time.";
    return null;
  };

  const handleSave = async (row: AdjustmentRow) => {
    const validation = rowValidationError(row);
    if (validation) {
      patchRow(row.key, { error: validation });
      return;
    }
    patchRow(row.key, { saving: true, error: null });
    try {
      const saved = await createStaffHoursOverride({
        staff_id: staffId,
        work_date: row.date,
        // Both times are required — never send a null (the backend 400s).
        start_time: convertTo12HourFormat(row.start),
        end_time: convertTo12HourFormat(row.end),
        business_location_uuid: businessLocationUuid,
      });
      patchRow(row.key, {
        overrideId: saved?.id ?? row.overrideId,
        saving: false,
        dirty: false,
        touched: true,
        error: null,
      });
    } catch {
      patchRow(row.key, {
        saving: false,
        error: "Could not save this adjustment. Please try again.",
      });
    }
  };

  const handleDelete = async (row: AdjustmentRow) => {
    // Unsaved draft: just drop it locally.
    if (!row.overrideId) {
      setRows((prev) => prev.filter((r) => r.key !== row.key));
      return;
    }
    patchRow(row.key, { saving: true, error: null });
    try {
      await deleteStaffHoursOverride(row.overrideId);
      setRows((prev) => prev.filter((r) => r.key !== row.key));
    } catch {
      patchRow(row.key, {
        saving: false,
        error: "Could not remove this adjustment. Please try again.",
      });
    }
  };

  return (
    <Box className={styles.container}>
      <Box>
        <UnstyledButton
          className={styles.sectionHeader}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          <Text component="span" className={styles.heading}>
            Schedule Adjustments{rows.length > 0 ? ` (${rows.length})` : ""}
          </Text>
          <IconChevronDown
            size={18}
            className={`${styles.chevron} ${expanded ? styles.chevronOpen : ""}`}
          />
        </UnstyledButton>
        <Text className={styles.helper}>
          One-off hours for a single date. These overwrite the weekly schedule
          above, including days marked Unavailable.
        </Text>
      </Box>

      {loading ? (
        <Group justify="center" py="sm">
          <Loader size="sm" color="var(--brand-teal)" />
        </Group>
      ) : (
        <>
          <Collapse in={expanded}>
            <Box className={styles.rowList}>
              {loadError && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  variant="light"
                >
                  {loadError}
                </Alert>
              )}

              {rows.map((row) => {
                const validation = rowValidationError(row);
                const saveDisabled =
                  row.saving || !row.dirty || validation != null;
                return (
                  <Box key={row.key} className={styles.row}>
                    <Box className={styles.line1}>
                      <DateInput
                        className={styles.dateField}
                        aria-label="Adjustment date"
                        placeholder="Select date"
                        valueFormat="MMM D, YYYY"
                        value={row.date || null}
                        onChange={(value) => handleDateChange(row, value)}
                        disabled={row.saving}
                        popoverProps={{ withinPortal: true }}
                      />

                      <Box className={styles.times}>
                        <TimeInput
                          className={styles.timeField}
                          aria-label="Start time"
                          withSeconds={false}
                          step={900}
                          value={row.start || ""}
                          onChange={(e) =>
                            handleTimeChange(
                              row,
                              "start",
                              e.currentTarget.value,
                            )
                          }
                          disabled={row.saving}
                        />
                        <TimeInput
                          className={styles.timeField}
                          aria-label="End time"
                          withSeconds={false}
                          step={900}
                          value={row.end || ""}
                          onChange={(e) =>
                            handleTimeChange(row, "end", e.currentTarget.value)
                          }
                          disabled={row.saving}
                        />
                      </Box>

                      <Tooltip label="Remove adjustment" withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Remove adjustment"
                          onClick={() => handleDelete(row)}
                          disabled={row.saving}
                        >
                          <IconTrash size={15} stroke={2} />
                        </ActionIcon>
                      </Tooltip>
                    </Box>

                    <Box className={styles.line2}>
                      <Box className={styles.contextGroup}>
                        <Text component="span" className={styles.context}>
                          {contextLine(row.date, weeklySchedule)}
                        </Text>
                        <Text component="span" className={styles.tag}>
                          {tagFor(row.date, weeklySchedule)}
                        </Text>
                      </Box>

                      <Box className={styles.rowActions}>
                        {(row.dirty || !row.overrideId) && (
                          <Button
                            size="xs"
                            color="var(--brand-teal)"
                            loading={row.saving}
                            disabled={saveDisabled}
                            onClick={() => handleSave(row)}
                          >
                            Save
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {row.error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        variant="light"
                      >
                        {row.error}
                      </Alert>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Collapse>

          <Box
            component="button"
            type="button"
            className={styles.addRow}
            onClick={handleAdd}
          >
            + Add Adjustment
          </Box>
        </>
      )}
    </Box>
  );
}
